import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Globe, ChevronDown, LogOut, User, MapPin, Menu, X, ArrowRight,
  LayoutDashboard, ClipboardList, ShieldCheck, Wallet, Zap, Calendar, ShieldAlert,
  UserRound, LifeBuoy, KeyRound,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { LANGUAGES, CURRENCIES, TRANSLATIONS } from "@/lib/api";
import CleanzaLogo from "@/components/CleanzaLogo";
import NotificationsBell from "@/components/NotificationsBell";

// Public marketing nav — shown ONLY to logged-out visitors.
const PUBLIC_NAV = [
  { label: "Home",         href: "/#top"          },
  { label: "About",        href: "/#about"        },
  { label: "Services",     href: "/services"      },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Pricing",      href: "/#pricing"      },
  { label: "Contact",      href: "/#contact"      },
];

// Role-aware nav for authenticated users.
const CLIENT_NAV = [
  { label: "Dashboard",   to: "/dashboard",         icon: LayoutDashboard },
  { label: "My Bookings", to: "/dashboard/bookings", icon: ClipboardList  },
  { label: "Book",        to: "/book",              icon: Calendar        },
  { label: "Profile",     to: "/dashboard/profile", icon: UserRound       },
  { label: "Help",        to: "/support",           icon: LifeBuoy        },
];

const CLEANER_NAV = [
  { label: "Cleaner Portal",   to: "/cleaner",                  icon: LayoutDashboard },
  { label: "Available Jobs",   to: "/cleaner/available",        icon: Zap             },
  { label: "Active / Schedule", to: "/cleaner/schedule",        icon: Calendar        },
  { label: "Earnings",         to: "/cleaner/earnings",         icon: Wallet          },
  { label: "Safety Checklist", to: "/cleaner/safety-checklist", icon: ShieldAlert     },
  { label: "Help",             to: "/support",                  icon: LifeBuoy        },
];

export default function Navbar() {
  const { user, logout, language, setLanguage, currency, setCurrency, country } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const t = TRANSLATIONS[language] || TRANSLATIONS.en;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("#top");

  const dashPath = user
    ? (user.role === "admin" ? "/admin" : user.role === "cleaner" ? "/cleaner" : "/dashboard")
    : "/login";

  useEffect(() => {
    const h = loc.hash || "#top";
    setActiveHash(h);
    if (loc.hash) {
      const id = loc.hash.slice(1);
      if (id === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    } else if (loc.pathname === "/") setActiveHash("#top");
  }, [loc.pathname, loc.hash]);

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    // Plain path (no #hash) — just navigate.
    if (!href.includes("#")) {
      setActiveHash("");
      nav(href);
      return;
    }
    const [path, hash] = href.split("#");
    setActiveHash(`#${hash}`);
    if (loc.pathname !== (path || "/")) {
      nav(`${path || "/"}#${hash || ""}`);
    } else {
      if (!hash || hash === "top") window.scrollTo({ top: 0, behavior: "smooth" });
      else {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const isActive = (href) => {
    // Plain path match (e.g. /services)
    if (!href.includes("#")) return loc.pathname === href;
    const h = href.split("#")[1] || "top";
    return loc.pathname === "/" && (activeHash === `#${h}` || (h === "top" && (activeHash === "" || activeHash === "#top")));
  };

  const handleLogout = () => {
    setMobileOpen(false);
    logout();
    nav("/", { replace: true });
  };

  const roleNav = user ? (user.role === "cleaner" ? CLEANER_NAV : user.role === "customer" ? CLIENT_NAV : []) : [];

  return (
    <header className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-xl border-b border-white/5" data-testid="site-header">
      <div className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-teal-500/60 to-transparent" />

      {/* ROW 1 — Brand */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-4 sm:pt-5 sm:pb-5">
        <div className="flex items-center gap-4 sm:gap-6 lg:gap-8">
          <Link to={user ? dashPath : "/"} data-testid="nav-logo" onClick={() => setMobileOpen(false)} className="flex-shrink-0 group">
            <CleanzaLogo variant="icon" height={92} className="hidden sm:block drop-shadow-[0_0_28px_rgba(20,184,166,0.35)] group-hover:drop-shadow-[0_0_36px_rgba(20,184,166,0.55)] transition-all" />
            <CleanzaLogo variant="icon" height={60} className="sm:hidden drop-shadow-[0_0_18px_rgba(20,184,166,0.35)]" />
          </Link>
          <div className="hidden md:block self-stretch w-px bg-gradient-to-b from-transparent via-white/25 to-transparent" />
          <div className="hidden md:flex flex-1 flex-col items-start justify-center select-none" data-testid="brand-lockup">
            <div className="font-display font-black leading-none tracking-[-0.01em] text-4xl lg:text-5xl xl:text-[3.75rem] flex items-baseline">
              <span className="bg-gradient-to-b from-white via-slate-100 to-slate-400 bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(255,255,255,0.15)]">CLEAN</span>
              <span className="bg-gradient-to-b from-teal-300 via-teal-400 to-teal-600 bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(20,184,166,0.55)]">BAZ</span>
            </div>
            <div className="mt-2.5 flex items-center gap-3">
              <span className="h-px w-8 lg:w-12 bg-gradient-to-r from-transparent to-white/60" />
              <span className="font-mono uppercase text-[10px] lg:text-[11px] tracking-[0.42em] text-slate-100">Cleaning&nbsp;Services</span>
              <span className="h-px w-8 lg:w-12 bg-gradient-to-l from-transparent to-white/60" />
            </div>
            <div className="mt-2 font-mono uppercase text-[10px] lg:text-[11px] tracking-[0.34em] text-teal-400 self-center">
              Clean.&nbsp;Fresh.&nbsp;Done.<sup className="text-[7px] lg:text-[8px] ml-0.5">™</sup>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
            <div className="hidden xl:flex items-center gap-1.5 text-xs font-mono text-slate-400 px-3 py-1.5 rounded-full bg-white/5 border border-white/10" data-testid="nav-country">
              <MapPin className="w-3 h-3 text-teal-400" /><span>{country}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="nav-lang-trigger" className="gap-1.5 text-slate-200 hover:text-teal-300 hover:bg-white/5">
                  <Globe className="w-4 h-4" /><span className="text-sm">{LANGUAGES[language]?.label}</span><ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border border-white/10 shadow-2xl rounded-2xl p-1.5 w-44 text-slate-100">
                <DropdownMenuLabel className="text-xs font-mono uppercase tracking-wider text-slate-500">Language</DropdownMenuLabel>
                {Object.entries(LANGUAGES).map(([code, l]) => (
                  <DropdownMenuItem key={code} onClick={() => setLanguage(code)} data-testid={`lang-${code}`} className="cursor-pointer focus:bg-white/10 focus:text-teal-300">
                    <span className="font-mono text-xs mr-2 text-slate-500">{l.flag}</span>{l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="nav-currency-trigger" className="gap-1.5 text-slate-200 hover:text-teal-300 hover:bg-white/5">
                  <span className="font-mono">{CURRENCIES[currency]?.symbol}</span><span className="text-sm">{currency}</span><ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border border-white/10 shadow-2xl rounded-2xl p-1.5 w-40 text-slate-100">
                <DropdownMenuLabel className="text-xs font-mono uppercase tracking-wider text-slate-500">Currency</DropdownMenuLabel>
                {Object.entries(CURRENCIES).map(([code, c]) => (
                  <DropdownMenuItem key={code} onClick={() => setCurrency(code)} data-testid={`curr-${code}`} className="cursor-pointer focus:bg-white/10 focus:text-teal-300">
                    <span className="font-mono mr-2">{c.symbol}</span>{code}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <>
                <NotificationsBell />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="nav-user-trigger" className="gap-2 bg-transparent border-white/15 text-slate-100 hover:border-teal-400 hover:text-teal-300 hover:bg-white/5">
                    <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-teal-300" />
                    </div>
                    <span className="text-sm max-w-[100px] truncate">{user.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border border-white/10 shadow-2xl rounded-2xl p-1.5 w-60 text-slate-100">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user.full_name}</div>
                    <div className="text-xs text-slate-400 truncate">{user.email}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-teal-400 mt-1">{user.role}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {roleNav.map((n) => (
                    <DropdownMenuItem key={n.to} onClick={() => nav(n.to)} data-testid={`nav-menu-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[\/]/g, "-")}`} className="cursor-pointer focus:bg-white/10">
                      <n.icon className="w-4 h-4 mr-2 opacity-80" />{n.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onClick={() => nav("/account/security")} data-testid="nav-menu-security" className="cursor-pointer focus:bg-white/10">
                    <KeyRound className="w-4 h-4 mr-2 opacity-80" />Change password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout" className="cursor-pointer text-red-400 focus:bg-white/10">
                    <LogOut className="w-4 h-4 mr-2" />Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="nav-login" className="text-slate-200 hover:text-teal-300 hover:bg-white/5">
                {t.login}
              </Button>
            )}
          </div>

          <button
            className="md:hidden ml-auto inline-flex items-center justify-center w-10 h-10 rounded-xl border border-white/15 text-slate-100 hover:text-teal-300 hover:border-teal-400 transition-colors"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile compact lock-up */}
        <div className="md:hidden mt-3 flex flex-col items-center select-none" data-testid="brand-lockup-mobile">
          <div className="font-display font-black leading-none tracking-[-0.01em] text-3xl flex items-baseline">
            <span className="bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent">CLEAN</span>
            <span className="bg-gradient-to-b from-teal-300 to-teal-500 bg-clip-text text-transparent">BAZ</span>
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="h-px w-6 bg-gradient-to-r from-transparent to-white/60" />
            <span className="font-mono uppercase text-[9px] tracking-[0.38em] text-slate-100">Cleaning&nbsp;Services</span>
            <span className="h-px w-6 bg-gradient-to-l from-transparent to-white/60" />
          </div>
          <div className="mt-1 font-mono uppercase text-[9px] tracking-[0.32em] text-teal-400">
            Clean.&nbsp;Fresh.&nbsp;Done.<sup className="text-[6px] ml-0.5">™</sup>
          </div>
        </div>
      </div>

      {/* ROW 2 — Nav bar (desktop). Public items for logged-out; role items for logged-in. */}
      <nav className="hidden md:block border-t border-white/5" data-testid="site-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ul className="flex items-center justify-between gap-1 py-3">
            {!user ? (
              <>
                <div className="flex items-center gap-1 lg:gap-2">
                  {PUBLIC_NAV.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <li key={item.label}>
                        <a
                          href={item.href}
                          onClick={(e) => handleAnchor(e, item.href)}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`relative inline-flex items-center px-3 lg:px-4 py-2 text-[13px] lg:text-sm font-medium transition-colors uppercase tracking-[0.16em] ${active ? "text-teal-300" : "text-slate-100 hover:text-teal-300"}`}
                        >
                          {item.label}
                          {active && <span className="absolute left-3 lg:left-4 right-3 lg:right-4 -bottom-0.5 h-[2px] bg-teal-400 rounded-full shadow-[0_0_10px_rgba(20,184,166,0.7)]" />}
                        </a>
                      </li>
                    );
                  })}
                </div>
                <li>
                  <button
                    onClick={() => { setMobileOpen(false); nav("/signup"); }}
                    data-testid="nav-get-started"
                    className="group inline-flex items-center gap-2 pl-5 pr-4 py-2.5 rounded-full bg-transparent border border-teal-400/70 text-teal-200 hover:text-white hover:bg-teal-500/10 hover:border-teal-300 shadow-[inset_0_0_20px_rgba(20,184,166,0.15),0_0_24px_rgba(20,184,166,0.25)] hover:shadow-[inset_0_0_28px_rgba(20,184,166,0.25),0_0_36px_rgba(20,184,166,0.55)] transition-all font-semibold text-sm tracking-wide"
                  >
                    Get Started <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </li>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1 lg:gap-2" data-testid="site-nav-role">
                  {roleNav.map((n) => {
                    const active = loc.pathname === n.to.split("#")[0] && (!n.to.includes("#") || loc.hash === "#" + n.to.split("#")[1]);
                    return (
                      <li key={n.to}>
                        <button
                          onClick={() => { setMobileOpen(false); nav(n.to); }}
                          data-testid={`nav-role-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[\/]/g, "-")}`}
                          className={`relative inline-flex items-center gap-1.5 px-3 lg:px-4 py-2 text-[13px] lg:text-sm font-medium transition-colors uppercase tracking-[0.16em] ${active ? "text-teal-300" : "text-slate-100 hover:text-teal-300"}`}
                        >
                          <n.icon className="w-4 h-4 opacity-70" />{n.label}
                          {active && <span className="absolute left-3 lg:left-4 right-3 lg:right-4 -bottom-0.5 h-[2px] bg-teal-400 rounded-full shadow-[0_0_10px_rgba(20,184,166,0.7)]" />}
                        </button>
                      </li>
                    );
                  })}
                </div>
                <li>
                  <button
                    onClick={handleLogout}
                    data-testid="nav-signout"
                    className="group inline-flex items-center gap-2 pl-5 pr-4 py-2.5 rounded-full bg-transparent border border-red-400/50 text-red-200 hover:text-white hover:bg-red-500/10 hover:border-red-300 transition-all font-semibold text-sm tracking-wide"
                  >
                    <LogOut className="w-4 h-4" />Sign out
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </nav>

      {/* Mobile drawer — role-aware */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-slate-950/98 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {!user ? (
              <>
                {PUBLIC_NAV.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={(e) => handleAnchor(e, item.href)}
                      data-testid={`nav-m-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium uppercase tracking-[0.14em] ${active ? "text-teal-300 bg-teal-500/10" : "text-slate-100 hover:text-teal-300 hover:bg-white/5"}`}
                    >{item.label}</a>
                  );
                })}
                <div className="flex items-center gap-2 pt-3 mt-1 border-t border-white/10">
                  <Button size="sm" variant="ghost" onClick={() => { setMobileOpen(false); nav("/login"); }} data-testid="nav-mobile-login" className="flex-1 text-slate-200 hover:bg-white/5">{t.login}</Button>
                  <button
                    onClick={() => { setMobileOpen(false); nav("/signup"); }}
                    data-testid="nav-mobile-get-started"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-transparent border border-teal-400/70 text-teal-200 hover:text-white hover:bg-teal-500/10 shadow-[0_0_20px_rgba(20,184,166,0.35)] transition-all font-semibold text-sm"
                  >Get Started <ArrowRight className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <>
                <div className="px-3 py-2 mb-1 rounded-lg bg-white/5" data-testid="mobile-user-card">
                  <div className="text-sm text-slate-100 font-semibold truncate">{user.full_name}</div>
                  <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-teal-400 mt-0.5">{user.role}</div>
                </div>
                {roleNav.map((n) => (
                  <button
                    key={n.to}
                    onClick={() => { setMobileOpen(false); nav(n.to); }}
                    data-testid={`nav-m-role-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[\/]/g, "-")}`}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium uppercase tracking-[0.14em] text-slate-100 hover:text-teal-300 hover:bg-white/5 text-left"
                  >
                    <n.icon className="w-4 h-4 opacity-70" />{n.label}
                  </button>
                ))}
                <button
                  onClick={handleLogout}
                  data-testid="nav-m-signout"
                  className="mt-2 flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-red-400/40"
                >
                  <LogOut className="w-4 h-4" />Sign out
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
